//third party imports
import { Injectable } from '@angular/core';

import { AuthenticationResult, EventMessage } from '@azure/msal-browser';

//local imports
import { ApplicationConfiguration } from '@core/configuration';
import { AuthenticationStage } from '@core/messages';

import { AuthDenialCode } from './auth-denial.codes';
import { TenantClaimResolverService } from '@core/security/services/tenant-claim-resolver.service';

@Injectable({
    providedIn: 'root'
})
export class AuthEventContextHelper {
    constructor(
        private applicationConfig: ApplicationConfiguration,
        private tenantClaimResolver: TenantClaimResolverService,
    ) { }

    public resolveLoggingInStageFromAuthResult(
        message: EventMessage,
        authenticationResult: AuthenticationResult,
    ): AuthenticationStage {
        const claims = (authenticationResult.account?.idTokenClaims as Record<string, unknown> | undefined)
            || (authenticationResult.idTokenClaims as Record<string, unknown> | undefined)
            || {};

        const policy = (this.tenantClaimResolver.resolveStringClaim(claims, 'tfp') || this.tenantClaimResolver.resolveStringClaim(claims, 'acr') || '').trim().toLowerCase();
        const authority = this.resolveAuthorityFromEventMessage(message);
        const switchTenantPolicy = (this.applicationConfig.azureB2c?.switchTenant || '').trim().toLowerCase();

        if (switchTenantPolicy.length > 0) {
            const authorityContainsSwitchPolicy = authority.includes(`/${switchTenantPolicy}/`);
            if (policy === switchTenantPolicy || authorityContainsSwitchPolicy) {
                return AuthenticationStage.SwitchTenant;
            }
        }

        return AuthenticationStage.FreshSignIn;
    }

    public buildAuthEventDeduplicationKey(message: EventMessage): string {
        const authenticationResult = message.payload as AuthenticationResult | undefined;
        const account = authenticationResult?.account;
        const accountId = account?.homeAccountId || account?.localAccountId || account?.username || '';

        const accountClaims = account?.idTokenClaims as Record<string, unknown> | undefined;
        const tokenClaims = authenticationResult?.idTokenClaims as Record<string, unknown> | undefined;
        const claims = accountClaims || tokenClaims;

        const tenant = this.tenantClaimResolver.resolveTenantFromClaims(claims)?.toLowerCase() || '';
        const policy = (this.tenantClaimResolver.resolveStringClaim(claims || {}, 'tfp') || this.tenantClaimResolver.resolveStringClaim(claims || {}, 'acr') || '').toLowerCase();
        const authority = this.resolveAuthorityFromEventMessage(message);

        return [
            message.eventType,
            accountId,
            policy || authority,
            tenant,
        ].join('|');
    }

    public resolveAuthorityFromEventMessage(message: EventMessage): string {
        const payload = message.payload as Record<string, unknown> | undefined;
        const authority = typeof payload?.['authority'] === 'string' ? payload['authority'] as string : '';
        return authority.trim().toLowerCase();
    }

    public resolveAuthDenialCode(error: unknown): string | undefined {
        const asRecord = error as Record<string, unknown> | undefined;
        const directCode = typeof asRecord?.['errorCode'] === 'string'
            ? (asRecord?.['errorCode'] as string).trim()
            : '';

        if (directCode.length > 0) {
            return directCode.toUpperCase();
        }

        const message = typeof asRecord?.['message'] === 'string'
            ? (asRecord?.['message'] as string)
            : '';

        if (!message) {
            return undefined;
        }

        const codeMatch = message.match(/errorCode\s*[:=]\s*([A-Z0-9_\-]+)/i);
        if (codeMatch && codeMatch[1]) {
            return codeMatch[1].trim().toUpperCase();
        }

        return AuthDenialCode.Unknown;
    }
}
